import { useCallback } from 'react';

import { Practitioner } from '@dreryk/egabinet-private-medical-service-frontend-api';

import {
   InteractiveTable,
   InteractiveTableSelectorItemFilter,
   InteractiveTableSelectorItemKeyExtractor,
   InteractiveTableSelectorItemRenderer,
   InteractiveTableSelectorMapper,
   InteractiveTableSelectorTableKeyExtractor,
   Text,
} from '@dreryk/react-components';

import { SpecialtiesText } from '../../../../shared/specialties/SpecialtiesText';
import { medicalServiceTranslationKeys } from '../../../../translations';
import { MedicalServiceFormBean, MedicalServiceFormController } from '../../form/medicalServiceForm.types';
import { MedicalServicePractitionerBean } from '../practitioner.types';
import { mapPractitionerToMedicalServicePractitionerBean } from '../practitioner.utils';

const t = medicalServiceTranslationKeys.shared.practitioner.table.selector;

type MedicalServicePractitionerInteractiveTableSelectorProps = Pick<
   MedicalServiceFormController,
   'register' | 'control'
> & {
   readonly practitioners: Practitioner[];
};

// eslint-disable-next-line max-lines-per-function
export const MedicalServicePractitionerInteractiveTableSelector = (
   props: MedicalServicePractitionerInteractiveTableSelectorProps
): JSX.Element => {
   const mapper = useCallback<InteractiveTableSelectorMapper<Practitioner, MedicalServicePractitionerBean>>(
      (practitioner) => {
         const deps: MedicalServiceFormBean = props.control.getValues();
         return mapPractitionerToMedicalServicePractitionerBean(deps)(practitioner);
      },
      [props.control]
   );

   const itemRenderer = useCallback<InteractiveTableSelectorItemRenderer<Practitioner>>((practitioner, Highlight) => {
      return (
         <span>
            <Highlight
               node={
                  <Text testId={'fullName'}>
                     {practitioner.firstName.asString()} {practitioner.lastName.asString()}
                  </Text>
               }
            />
            {', '}
            <Highlight node={<SpecialtiesText specialties={practitioner.specialtyNames} secondary />} />
         </span>
      );
   }, []);

   const itemKeyExtractor = useCallback<InteractiveTableSelectorItemKeyExtractor<Practitioner>>((practitioner) => {
      return practitioner.id.toString();
   }, []);

   const tableKeyExtractor = useCallback<InteractiveTableSelectorTableKeyExtractor<MedicalServicePractitionerBean>>(
      (value) => {
         return value.id.toString();
      },
      []
   );

   const itemFilter = useCallback<InteractiveTableSelectorItemFilter<Practitioner>>((item) => {
      return [item.firstName.asString(), item.lastName.asString(), ...item.specialtyNames];
   }, []);

   return (
      <InteractiveTable.Selector<Practitioner, MedicalServicePractitionerBean, MedicalServiceFormBean>
         register={props.register}
         testId={'practitionersSelector'}
         name={'practitionersSelector'}
         label={t.label}
         placeholder={t.placeholder}
         items={props.practitioners}
         itemKeyExtractor={itemKeyExtractor}
         tableKeyExtractor={tableKeyExtractor}
         itemRenderer={itemRenderer}
         itemFilter={itemFilter}
         mapper={mapper}
         required
      />
   );
};
